import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users as UsersIcon,
  RefreshCw,
  Search,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Clock,
  AlertCircle,
  X,
  Plus,
  ExternalLink,
} from 'lucide-react';
import {
  credits,
  CreditUser,
  CreditBalanceDetail,
  CreditTransaction,
} from '../../api/client';
import { formatNumber, formatRelativeTime, cn } from '../../lib/utils';

const PAGE_SIZE = 20;

export default function CreditsUsers() {
  const [users, setUsers] = useState<CreditUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [userDetail, setUserDetail] = useState<CreditBalanceDetail | null>(null);
  const [userTransactions, setUserTransactions] = useState<CreditTransaction[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [granting, setGranting] = useState(false);
  const detailRequestId = useRef(0);
  const expandedUserRef = useRef<string | null>(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await credits.users({
        search: search || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setUsers(res.users);
      setTotal(res.total);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(0);
    fetchUsers();
  };

  const handleExpand = async (deviceId: string) => {
    if (expandedUserRef.current === deviceId) {
      detailRequestId.current += 1;
      expandedUserRef.current = null;
      setExpandedUser(null);
      setUserDetail(null);
      setUserTransactions([]);
      setDetailLoading(false);
      return;
    }

    const requestId = detailRequestId.current + 1;
    detailRequestId.current = requestId;
    expandedUserRef.current = deviceId;
    setExpandedUser(deviceId);
    setUserDetail(null);
    setUserTransactions([]);
    setDetailLoading(true);
    try {
      const [balanceRes, historyRes] = await Promise.all([
        credits.balance(deviceId),
        credits.history(deviceId, { limit: 10 }),
      ]);
      if (detailRequestId.current === requestId && expandedUserRef.current === deviceId) {
        setUserDetail(balanceRes);
        setUserTransactions(historyRes.transactions);
      }
    } catch (err) {
      console.error('Failed to fetch user detail:', err);
    } finally {
      if (detailRequestId.current === requestId && expandedUserRef.current === deviceId) {
        setDetailLoading(false);
      }
    }
  };

  const handleQuickGrant = async (deviceId: string, amount: number) => {
    if (!confirm(`Grant ${amount} credits to ${deviceId.slice(0, 12)}...?`)) return;
    setGranting(true);
    try {
      await credits.grant({
        device_id: deviceId,
        amount,
        reason: 'manual_topup',
        note: `Quick grant from admin UI`,
      });
      // Refresh the expanded detail
      if (expandedUserRef.current === deviceId) {
        const requestId = detailRequestId.current + 1;
        detailRequestId.current = requestId;
        const [balanceRes, historyRes] = await Promise.all([
          credits.balance(deviceId),
          credits.history(deviceId, { limit: 10 }),
        ]);
        if (detailRequestId.current === requestId && expandedUserRef.current === deviceId) {
          setUserDetail(balanceRes);
          setUserTransactions(historyRes.transactions);
        }
      }
      fetchUsers();
    } catch (err) {
      console.error('Failed to grant:', err);
      setError(err instanceof Error ? err.message : 'Grant failed');
    } finally {
      setGranting(false);
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-muted-foreground">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading users...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-3xl font-bold text-gradient">Credit Users</h1>
          <p className="text-muted-foreground mt-1">{total} users with credit balances</p>
        </div>
        <button
          onClick={fetchUsers}
          disabled={loading}
          className="btn-secondary px-4 py-2 rounded-xl flex items-center gap-2 text-sm"
        >
          <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="animate-fade-in-up animate-delay-100">
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by device ID..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-secondary border border-border/50 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
            />
          </div>
          <button
            type="submit"
            className="px-6 py-2.5 rounded-xl bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 text-sm font-medium transition-colors"
          >
            Search
          </button>
        </div>
      </form>

      {/* Error */}
      {error && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-5 animate-scale-in">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-rose-300/80 flex-1">{error}</p>
            <button onClick={() => setError(null)} className="p-1 rounded-lg hover:bg-rose-500/20 text-rose-400">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="card-glow rounded-2xl overflow-hidden animate-fade-in-up animate-delay-200">
        <table className="table-neural min-w-full">
          <thead>
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider w-8" />
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Device ID</th>
              <th className="px-6 py-4 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Balance</th>
              <th className="px-6 py-4 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Used</th>
              <th className="px-6 py-4 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Refunded</th>
              <th className="px-6 py-4 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Last Active</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              const detailForUser = expandedUser === user.device_id && userDetail?.device_id === user.device_id
                ? userDetail
                : null;

              return (
                <UserRow
                  key={user.device_id}
                  user={user}
                  isExpanded={expandedUser === user.device_id}
                  onToggle={() => handleExpand(user.device_id)}
                  detail={detailForUser}
                  transactions={detailForUser ? userTransactions : []}
                  detailLoading={detailLoading && expandedUser === user.device_id}
                  onQuickGrant={handleQuickGrant}
                  granting={granting}
                />
              );
            })}
            {users.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-16 text-center">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center">
                      <UsersIcon className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <p className="text-muted-foreground">No users found</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between animate-fade-in-up animate-delay-300">
          <p className="text-sm text-muted-foreground">
            Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className={cn(
                'p-2 rounded-lg bg-secondary text-muted-foreground transition-colors',
                page === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:text-foreground'
              )}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-muted-foreground px-2">
              Page {page + 1} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className={cn(
                'p-2 rounded-lg bg-secondary text-muted-foreground transition-colors',
                page >= totalPages - 1 ? 'opacity-50 cursor-not-allowed' : 'hover:text-foreground'
              )}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function UserRow({
  user,
  isExpanded,
  onToggle,
  detail,
  transactions,
  detailLoading,
  onQuickGrant,
  granting,
}: {
  user: CreditUser;
  isExpanded: boolean;
  onToggle: () => void;
  detail: CreditBalanceDetail | null;
  transactions: CreditTransaction[];
  detailLoading: boolean;
  onQuickGrant: (deviceId: string, amount: number) => void;
  granting: boolean;
}) {
  const navigate = useNavigate();
  return (
    <>
      <tr
        className="cursor-pointer hover:bg-secondary/50 transition-colors"
        onClick={onToggle}
      >
        <td className="px-6 py-4">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-emerald-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          )}
        </td>
        <td className="px-6 py-4">
          <code className="text-xs text-muted-foreground font-mono" title={user.device_id}>
            {user.device_id.slice(0, 8)}...{user.device_id.slice(-4)}
          </code>
        </td>
        <td className="px-6 py-4 text-right">
          <span className={cn('font-mono font-medium', user.balance > 0 ? 'text-emerald-400' : 'text-rose-400')}>
            {formatNumber(user.balance)}
          </span>
        </td>
        <td className="px-6 py-4 text-right font-mono text-sm text-muted-foreground">
          {formatNumber(user.total_used)}
        </td>
        <td className="px-6 py-4 text-right font-mono text-sm text-muted-foreground">
          {formatNumber(user.total_refunded)}
        </td>
        <td className="px-6 py-4 text-right">
          <div className="flex items-center justify-end gap-2 text-sm text-muted-foreground">
            <Clock className="w-3.5 h-3.5" />
            {formatRelativeTime(user.updated_at)}
          </div>
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={6} className="px-6 py-0">
            <div className="py-4 pl-8 border-l-2 border-emerald-500/30 ml-4 space-y-4">
              {detailLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground py-4">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Loading...
                </div>
              ) : detail ? (
                <>
                  {/* Detail Info */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <InfoItem label="Full ID" value={detail.device_id} mono />
                    <InfoItem label="Balance" value={formatNumber(detail.balance)} />
                    <InfoItem label="Granted" value={formatNumber(detail.total_granted)} />
                    <InfoItem label="Purchased" value={`${formatNumber(detail.total_purchased)} (${detail.purchase_count} txn${detail.purchase_count !== 1 ? 's' : ''})`} />
                    <InfoItem label="Used" value={formatNumber(detail.total_used)} />
                    <InfoItem label="Refunded" value={formatNumber(detail.total_refunded)} />
                    <InfoItem label="Apple ID" value={detail.apple_user_id || '(not linked)'} />
                    <InfoItem label="Attested" value={detail.is_attested ? 'Yes' : 'No'} />
                    <InfoItem label="Pending" value={String(detail.pending_reservations)} />
                  </div>

                  {/* Quick Grant */}
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">Quick Grant</p>
                    <div className="flex gap-2 flex-wrap">
                      {[100, 500, 1000].map((amount) => (
                        <button
                          key={amount}
                          onClick={(e) => { e.stopPropagation(); onQuickGrant(detail.device_id, amount); }}
                          disabled={granting}
                          className="px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 text-xs font-medium transition-colors flex items-center gap-1 disabled:opacity-50"
                        >
                          <Plus className="w-3 h-3" />
                          {amount}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Recent Transactions */}
                  {transactions.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Recent Transactions</p>
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/credits/generations?device_id=${detail.device_id}`); }}
                          className="flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 transition-colors"
                        >
                          View all
                          <ExternalLink className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="space-y-1.5">
                        {transactions.map((tx) => (
                          <div key={tx.id} className="flex items-center gap-3 text-xs py-1.5 px-3 rounded-lg bg-secondary/50">
                            <span className="text-muted-foreground w-16">
                              {new Date(tx.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            <TypeBadge type={tx.type} />
                            <span className={cn(
                              'font-mono font-medium w-14 text-right',
                              tx.amount > 0 ? 'text-emerald-400' : tx.amount === 0 ? 'text-muted-foreground' : 'text-rose-400'
                            )}>
                              {tx.amount > 0 ? '+' : ''}{tx.amount}
                            </span>
                            <span className="text-muted-foreground">{tx.gen_type || '—'}</span>
                            <span className="text-muted-foreground/70 ml-auto truncate max-w-[200px]">
                              {tx.reason || ''}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function InfoItem({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="text-[10px] text-muted-foreground/70 uppercase tracking-wider mb-0.5">{label}</p>
      <p className={cn('text-sm', mono ? 'font-mono text-xs break-all' : '')}>{value}</p>
    </div>
  );
}

function TypeBadge({ type }: { type: string }) {
  const styles: Record<string, string> = {
    grant: 'bg-emerald-500/15 text-emerald-400',
    purchase: 'bg-teal-500/15 text-teal-400',
    debit: 'bg-amber-500/15 text-amber-400',
    reserve: 'bg-violet-500/15 text-violet-400',
    refund: 'bg-cyan-500/15 text-cyan-400',
    free_chat: 'bg-sky-500/15 text-sky-400',
  };
  return (
    <span className={cn('px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider w-16 text-center', styles[type] || 'bg-secondary text-muted-foreground')}>
      {type === 'free_chat' ? 'free' : type}
    </span>
  );
}
